import { create } from "zustand";
import { createWhepSession, type WhepSession } from "../lib/whepSession";
import type { StreamSlot } from "../types/stream";
import logger from "../lib/logger";
import { useStreamStatusStore, findSlot } from "./streamStatusStore";

export type PipMode = "panel" | "floating" | "popped";
export type PipCorner = "tl" | "tr" | "bl" | "br";
export type PipSize = "sm" | "md";

/**
 * What the PiP store keeps about a slot it has a session for: only the fields
 * that never change over a slot's life, so they cannot go stale. Everything
 * that does change — `available`, `streamingUser`, `playStatus` — is read live
 * from `streamStatusStore`. The narrow type enforces that: there is no
 * `available` here to read out of date.
 */
export type SlotIdentity = Pick<
  StreamSlot,
  "id" | "streamContentType" | "stcId" | "gcId"
>;

export interface StreamPipState {
  /** One live WHEP session per slot currently being watched. */
  sessions: Record<number, WhepSession>;
  /** Identity of every slot a session has been opened for. */
  identities: Record<number, SlotIdentity>;
  /**
   * Slot ids with a panel tile currently mounted.
   *
   * A list, not a refcount: two tiles for the same slot would register once
   * and the first unmount would deregister both. Nothing mounts a slot twice
   * today — `ChannelsPanel` renders at
   * most one tile per slot id — so this stays a set-like list. Change it to a
   * `Record<number, number>` refcount before allowing a duplicate tile.
   */
  mounted: number[];
  focusedSlotId: number | null;
  mode: PipMode;
  corner: PipCorner;
  size: PipSize;
  muted: boolean;

  acquire: (slot: StreamSlot) => WhepSession;
  release: (slotId: number) => void;
  focus: (slotId: number) => void;
  switchTo: (slotId: number) => void;
  demote: () => void;
  /**
   * The server reports this slot free: its publisher stopped. Closes the
   * session and, if it was the mini player, dismisses it. Idempotent.
   */
  endSlot: (slotId: number) => void;
  close: () => void;
  setMode: (mode: PipMode) => void;
  setCorner: (corner: PipCorner) => void;
  setSize: (size: PipSize) => void;
  setMuted: (muted: boolean) => void;
}

const AVAILABILITY_POLL_MS = 20_000;

const initial = {
  sessions: {} as Record<number, WhepSession>,
  identities: {} as Record<number, SlotIdentity>,
  mounted: [] as number[],
  focusedSlotId: null as number | null,
  mode: "panel" as PipMode,
  corner: "br" as PipCorner,
  size: "md" as PipSize,
  muted: false,
};

function identityOf(slot: StreamSlot): SlotIdentity {
  return {
    id: slot.id,
    streamContentType: slot.streamContentType,
    stcId: slot.stcId,
    gcId: slot.gcId,
  };
}

/**
 * A slot no tile ever mounted is still reachable through the status store.
 * The slot's own `gcId` field can be missing (e.g. a partial event merge) —
 * fall back to the gc `findSlot` located it in, so `startWatch`'s
 * `identity?.gcId == null` guard doesn't silently skip the watchdog poll.
 */
function statusIdentity(slotId: number): SlotIdentity | undefined {
  const found = findSlot(useStreamStatusStore.getState().infoByGc, slotId);
  if (!found) return undefined;
  const identity = identityOf(found.slot);
  return identity.gcId != null ? identity : { ...identity, gcId: found.gcId };
}

/** Focused slot if it still has a live session, else first VIDEO, else first AUDIO. */
function pickFocus(state: StreamPipState): number | null {
  // A session can already be dead by the time the panel unmounts — promoting
  // it would show a card that never plays and never notices.
  const ids = Object.keys(state.sessions)
    .map(Number)
    .filter((id) => state.sessions[id].getState() !== "ended");
  if (ids.length === 0) return null;
  if (state.focusedSlotId != null && ids.includes(state.focusedSlotId))
    return state.focusedSlotId;
  const video = ids.find(
    (id) => state.identities[id]?.streamContentType === "VIDEO",
  );
  if (video != null) return video;
  return (
    ids.find((id) => state.identities[id]?.streamContentType === "AUDIO") ??
    null
  );
}

/**
 * Keep `keep`'s session plus every session a panel tile still holds; close the
 * rest. Sparing mounted slots matters on the explicit `⧉` path, where the panel
 * is still on screen: closing a sibling's peer connection there freezes a live
 * tile with no error and no recovery. `settle()` closes them once the panel
 * really unmounts, so the single-session-after-promotion invariant holds.
 */
function keepSessions(
  state: StreamPipState,
  keep: number | null,
): Record<number, WhepSession> {
  const sessions: Record<number, WhepSession> = {};
  for (const id of Object.keys(state.sessions).map(Number)) {
    if (id === keep || state.mounted.includes(id)) sessions[id] = state.sessions[id];
    else state.sessions[id].close();
  }
  return sessions;
}

export const useStreamPipStore = create<StreamPipState>((set, get) => {
  let settleScheduled = false;
  let unwatchSession: (() => void) | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * A floating session has no RabbitMQ feed telling it the stream ended, so it
   * watches the connection, and polls the slot as a backstop against a server
   * that holds the connection open on a frozen frame.
   */
  function startWatch(slotId: number) {
    stopWatch();
    const state = get();
    const session = state.sessions[slotId];
    const identity = state.identities[slotId];
    if (!session) return;

    const checkEnded = () => {
      if (session.getState() !== "ended") return false;
      logger.info("[PiP] Session for slot", slotId, "ended — closing");
      get().close();
      return true;
    };

    unwatchSession = session.subscribe(checkEnded);
    // `subscribe` only fires on change, so a session that died before we
    // started watching would never report it. Check the current state once.
    if (checkEnded()) return;

    if (identity?.stcId == null || identity?.gcId == null) return;
    const { stcId, gcId } = identity;
    pollTimer = setInterval(() => {
      // refresh() — not a bare fetch + setInfo — merges through anything an
      // event touched while this request was in flight. Without that merge a
      // STOP_STREAM (or a sibling's START_STREAM) that lands mid-poll would be
      // overwritten by this poll's now-stale answer when it resolves. If the
      // slot is free, the status subscription at the bottom of this file ends
      // the session — the same path a STOP_STREAM event takes.
      useStreamStatusStore
        .getState()
        .refresh(stcId, gcId)
        .catch(() => {
          /* refresh() already logs failures; this guards a future throw */
        });
    }, AVAILABILITY_POLL_MS);
  }

  function stopWatch() {
    unwatchSession?.();
    unwatchSession = null;
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  /**
   * Runs after an unmount batch has drained. React unmounts a panel's tiles one
   * by one, so only here can we tell the panel is really gone.
   *
   * Two jobs. When the last tile has gone the focused session is promoted into
   * the mini player. Either way, every session that no tile holds and that the
   * mini player is not showing is closed here — `release()` only ever schedules
   * this decision, so this is the single place a session's owner lets go of it.
   */
  function settle() {
    settleScheduled = false;
    const state = get();
    const promoting = state.mounted.length === 0;
    const keep = promoting
      ? pickFocus(state)
      : state.mode !== "panel"
        ? state.focusedSlotId
        : null;

    const sessions = keepSessions(state, keep);

    if (!promoting) {
      // The panel is still up; just drop what nothing owns any more.
      if (Object.keys(sessions).length !== Object.keys(state.sessions).length)
        set({ sessions });
      return;
    }

    if (keep == null) {
      stopWatch();
      set({ sessions, focusedSlotId: null, mode: "panel" });
      return;
    }

    logger.info("[PiP] Promoting slot", keep, "to the mini player");
    set({
      sessions,
      focusedSlotId: keep,
      // A popped-out window is already the mini player; a stray release must
      // not silently downgrade it and orphan the OS window.
      mode: state.mode === "popped" ? "popped" : "floating",
    });
    startWatch(keep);
  }

  return {
    ...initial,

    acquire: (slot) => {
      const state = get();
      const existing = state.sessions[slot.id];
      const session = existing ?? createWhepSession(slot);
      const isFreshMount = !state.mounted.includes(slot.id);
      // A fresh tile for the slot that is currently floating means the user
      // navigated back onto the panel: re-adopt it into the tile.
      const backToPanel =
        isFreshMount &&
        state.focusedSlotId === slot.id &&
        state.mode !== "panel";
      if (backToPanel) stopWatch();
      set({
        sessions: existing
          ? state.sessions
          : { ...state.sessions, [slot.id]: session },
        identities: { ...state.identities, [slot.id]: identityOf(slot) },
        mounted: isFreshMount ? [...state.mounted, slot.id] : state.mounted,
        mode: backToPanel ? "panel" : state.mode,
      });
      return session;
    },

    release: (slotId) => {
      set({ mounted: get().mounted.filter((id) => id !== slotId) });
      if (settleScheduled) return;
      settleScheduled = true;
      queueMicrotask(settle);
    },

    focus: (slotId) => set({ focusedSlotId: slotId }),

    switchTo: (slotId) => {
      const state = get();
      const identity = state.identities[slotId] ?? statusIdentity(slotId);
      if (!identity) return;
      const next = state.sessions[slotId] ?? createWhepSession(identity);
      // Same rule as demote(): a sibling whose tile is still on the panel keeps
      // its connection. settle() closes it when the panel really unmounts.
      set({
        sessions: { ...keepSessions(state, slotId), [slotId]: next },
        identities: { ...state.identities, [slotId]: identity },
        focusedSlotId: slotId,
      });
      if (get().mode !== "panel") startWatch(slotId);
    },

    demote: () => {
      const state = get();
      const keep = pickFocus(state);
      if (keep == null) return;
      set({
        sessions: keepSessions(state, keep),
        focusedSlotId: keep,
        mode: "floating",
      });
      startWatch(keep);
    },

    endSlot: (slotId) => {
      const state = get();
      const session = state.sessions[slotId];
      if (!session) return;
      session.close();

      const sessions = { ...state.sessions };
      delete sessions[slotId];

      if (state.focusedSlotId !== slotId) {
        set({ sessions });
        return;
      }
      stopWatch();
      set({ sessions, focusedSlotId: null, mode: "panel" });
    },

    close: () => {
      stopWatch();
      const state = get();
      for (const id of Object.keys(state.sessions).map(Number)) {
        state.sessions[id].close();
      }
      set({
        sessions: {},
        identities: {},
        focusedSlotId: null,
        mode: "panel",
      });
    },

    setMode: (mode) => set({ mode }),
    setCorner: (corner) => set({ corner }),
    setSize: (size) => set({ size }),
    setMuted: (muted) => set({ muted }),
  };
});

/**
 * The status store is the source of truth for whether a slot is live. When it
 * reports a slot free — its publisher stopped — end any session for it.
 *
 * zustand notifies subscribers synchronously inside `set()`, so this lands
 * before React renders the tile's unmount. That ordering is the whole fix:
 * otherwise the tile's `release()` looks exactly like the user navigating away,
 * and `settle()` promotes a dead stream into the mini player.
 */
useStreamStatusStore.subscribe((status) => {
  const pip = useStreamPipStore.getState();
  for (const key of Object.keys(pip.sessions)) {
    const slotId = Number(key);
    if (findSlot(status.infoByGc, slotId)?.slot.available) pip.endSlot(slotId);
  }
});

/** Closes every session and restores the initial state. Tests and logout. */
export function resetStreamPipStore(): void {
  useStreamPipStore.getState().close();
  useStreamPipStore.setState({ ...initial });
}
